// PeriodHead (timesheet rules §4, §5): the top of the Timesheet and of Hours. Where it was opened from,
// as a crumb (the Timesheet's "Hours › Sam Rivera"); the h1 ("Sam Rivera · Oct 12 – 25"); one line,
// "Payroll Fri Oct 30" or the locked and sent words; the facts, each once, a count above 0 in red; and
// what changes the page (the person, the period) at the end. A rule under it all. Reads only --phead-*.
// Until there's a pay period (QA-757) the pay line is one quiet line saying periods and locking come with
// payroll (PayrollLine), and the period's choice is ‹ This week › through payroll's weeks (WeekStep).
import type { ReactNode } from 'react';
import { Link } from '../../components/Link/Link.tsx';
import { Segmented } from '../../components/Segmented/Segmented.tsx';
import type { NoPayrollLine } from '../timesheet-view.ts';
import './PeriodHead.css';

export interface PeriodFact {
  label: string;
  value: string;
  tone?: 'bad';
}

export interface PeriodHeadProps {
  /** Where it was opened from: Links and words. */
  crumb?: ReactNode;
  title: string;
  /** "Payroll Fri Oct 30", or who locked it and sent it, when; or PayrollLine before payroll is set up. */
  payLine?: ReactNode;
  facts?: PeriodFact[];
  /** The person (SelectField) and the period (Segmented). */
  actions?: ReactNode;
  /** The title's id, for the page's aria-labelledby. */
  titleId?: string;
}

export function PeriodHead({ crumb, title, payLine, facts = [], actions, titleId }: PeriodHeadProps) {
  return (
    <header className="fl-phead">
      {crumb ? (
        <nav className="fl-phead__crumb" aria-label="Where you came from">
          {crumb}
        </nav>
      ) : null}
      <h1 id={titleId} className="fl-phead__title">
        {title}
      </h1>
      {payLine ? <p className="fl-phead__pay">{payLine}</p> : null}
      {facts.length ? (
        <dl className="fl-phead__facts">
          {facts.map((f) => (
            <div key={f.label} className="fl-phead__fact" data-tone={f.tone}>
              <dt className="fl-phead__label">{f.label}</dt>
              <dd className="fl-phead__value">{f.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {actions ? <div className="fl-phead__acts">{actions}</div> : null}
    </header>
  );
}

/** The quiet line before payroll is set up, its link for those who may set it up (Books › Payroll). */
export function PayrollLine({ line, onSetUp }: { line: NoPayrollLine; onSetUp?: (() => void) | undefined }) {
  return (
    <>
      {line.words}
      {line.link && onSetUp ? (
        <>
          {' '}
          <Link onPress={onSetUp}>{line.link}</Link>
        </>
      ) : null}
    </>
  );
}

export interface WeekStepProps {
  /** The week shown has today in it: there's no week after it yet. */
  isLatest: boolean;
  /** The week before (-1), this week (0), the week after (1). */
  onStep: (dir: -1 | 0 | 1) => void;
}

/** ‹ This week ›: payroll's weeks until there's a pay period, stepped as the board steps its days. */
export function WeekStep({ isLatest, onStep }: WeekStepProps) {
  return (
    <Segmented
      label="Week"
      selectionMode="none"
      items={[
        { id: 'earlier', label: 'The week before', icon: 'chevron-left' },
        { id: 'this', label: 'This week' },
        { id: 'later', label: 'The week after', icon: 'chevron-right', isDisabled: isLatest },
      ]}
      {...(isLatest ? { on: 'this' } : {})}
      onAction={(id) => onStep(id === 'earlier' ? -1 : id === 'later' ? 1 : 0)}
    />
  );
}
